<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import ProgressFrame from './layout/ProgressFrame.vue';
import ProgressBar from '../components/ProgressBar.vue';
import router from '@/router';
import { prepareRecommission } from '../utils/prepareRecommission';
import { RECOMMISSION_PREPARE_STEPS } from '../utils/constants';
import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';

/** Brief pause after prepare succeeds so meter.commissioned can clear before Searching. */
const SUCCESS_PAUSE_MS = 1500;
const SUCCESS_STEP_MESSAGE = 'Searching SolShare...';

const commissioningDeviceStore = useCommissioningDeviceStore();
const serialNumber = commissioningDeviceStore.serialNumber;

const prepareState = commissioningDeviceStore.recommissionPrepare;
const stepMessage = ref(
  prepareState.stepMessage || RECOMMISSION_PREPARE_STEPS[0]?.message || 'Preparing...',
);
const progressPercent = ref(prepareState.progressPercent);

let aborted = false;

const pause = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

onMounted(async () => {
  if (prepareState.status === 'success') {
    await router.replace({
      name: 'SearchingSolshare',
      params: { serialNumber },
    });
    return;
  }

  if (prepareState.status === 'failed') {
    await router.replace({ name: 'SearchingSolshareRecommissionPrepareFailed' });
    return;
  }

  try {
    await prepareRecommission(serialNumber, {
      onStep: (step) => {
        stepMessage.value = step.message;
      },
      onProgress: (percent) => {
        progressPercent.value = percent;
      },
      isAborted: () => aborted,
    });

    if (aborted) {
      return;
    }

    progressPercent.value = 100;
    stepMessage.value = SUCCESS_STEP_MESSAGE;
    commissioningDeviceStore.updateRecommissionPrepare({
      status: 'success',
      progressPercent: 100,
      stepMessage: SUCCESS_STEP_MESSAGE,
    });
    await pause(SUCCESS_PAUSE_MS);

    if (aborted) {
      return;
    }

    await router.replace({
      name: 'SearchingSolshare',
      params: { serialNumber },
    });
  } catch (error) {
    if (aborted) {
      return;
    }
    console.error('Recommission prepare failed', error);

    commissioningDeviceStore.updateRecommissionPrepare({ status: 'failed' });
    await router.replace({ name: 'SearchingSolshareRecommissionPrepareFailed' });
  }
});

onUnmounted(() => {
  aborted = true;
});
</script>

<template>
  <ProgressFrame pageClass="subsection-page" :titleTight="true" subTitleColor="secondary">
    <template #title>
      <div class="title-container">Preparing for recommissioning</div>
    </template>
    <template #subTitle>{{ serialNumber }}</template>

    <div class="content">
      <p class="instruction-text">Please wait while we're preparing the device.</p>
      <div class="test-progress-container" data-tn="recommission-prepare-progress">
        <ProgressBar
          title="Preparing SolShare..."
          titleTextAlign="left"
          :progress="progressPercent"
          :progressText="stepMessage"
        />
      </div>
    </div>
  </ProgressFrame>
</template>

<style lang="less" scoped>
.title-container {
  margin-top: 1rem;
  font-size: 2rem;
  font-weight: @font-weight-regular;
}

.content {
  text-align: center;

  .instruction-text {
    font-weight: @font-weight-medium;
  }

  .test-progress-container {
    width: 100%;
  }
}
</style>
